// Cloud backup mirror for the per-user state currently stored in
// localStorage. Writes go to both localStorage (the hot read path) and the
// Supabase `user_state` table (the recovery path). On a cold load we fetch
// the user's full cloud snapshot up front; if a key is missing in
// localStorage we hydrate from the snapshot so a cleared browser doesn't
// equal lost data.
//
// Everything in here is best-effort. If Supabase is unreachable, every
// function logs the failure and returns gracefully — the app keeps working
// on localStorage alone.

import { supabase } from './supabase';

// Cached snapshot fetched once per sign-in. Cleared on sign-out or when
// loadSnapshot is called with a different user id.
let snapshot = { userId: null, byKey: null };

// Fetch every row this user has in user_state. One round trip, regardless
// of how many keys the app uses. Safe to call multiple times; later calls
// re-fetch and replace the in-memory snapshot.
export async function loadSnapshot(userId) {
  if (!userId) {
    snapshot = { userId: null, byKey: null };
    return { ok: false, reason: 'no-user' };
  }
  try {
    // 8s hard watchdog. Supabase queries with a stale-but-not-expired
    // JWT can hang forever in the token-refresh loop — the try/catch
    // below only handles rejections, not never-resolves. Race it
    // against a timeout that resolves to a fake "error" so the caller
    // treats it as "no cloud data" and hydrates from localStorage.
    // See src/CommissionTracker.jsx tenant hydration effect — this
    // is the first await in the chain, so if it hangs the whole
    // "Loading your data…" gate never lifts.
    const queryPromise = supabase
      .from('user_state')
      .select('key, value')
      .eq('user_id', userId);
    const timeoutPromise = new Promise(resolve => {
      setTimeout(() => resolve({ data: null, error: { message: 'watchdog_timeout_8s' } }), 8000);
    });
    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
    if (error) {
      // Most common cause in the wild: the migration hasn't been applied
      // yet (`relation "user_state" does not exist`). Treat that as "no
      // cloud data" rather than a fatal error so the app boots normally.
      console.warn('[cloudBackup] loadSnapshot failed; falling back to localStorage only.', error.message);
      snapshot = { userId, byKey: {} };
      return { ok: false, reason: error.message === 'watchdog_timeout_8s' ? 'timeout' : 'fetch-error', error };
    }
    const byKey = {};
    (data || []).forEach(row => {
      if (row && row.key) byKey[row.key] = row.value;
    });
    snapshot = { userId, byKey };
    return { ok: true, count: Object.keys(byKey).length };
  } catch (err) {
    console.warn('[cloudBackup] loadSnapshot threw; falling back.', err);
    snapshot = { userId, byKey: {} };
    return { ok: false, reason: 'threw', error: err };
  }
}

// Read a value from the in-memory snapshot. Synchronous so the existing
// load effect doesn't need extra awaits. Returns undefined if the snapshot
// hasn't been loaded yet or the key isn't present.
export function snapshotGet(userId, key) {
  if (!snapshot.byKey || snapshot.userId !== userId) return undefined;
  return snapshot.byKey[key];
}

export function snapshotIsReady(userId) {
  return snapshot.byKey !== null && snapshot.userId === userId;
}

// Fire-and-forget upsert of a single key. Errors are logged but never
// propagated — localStorage is still the source of truth for the running
// session. The snapshot cache is updated optimistically so a same-session
// re-read sees the new value.
export function cloudSet(userId, key, value) {
  if (!userId || !key) return;
  // user_state.value is NOT NULL. Coerce undefined / null to an empty
  // object so we still record "the user touched this key" without
  // violating the column constraint. Without this guard we get a 409
  // every page load from features that haven't been configured yet
  // (e.g. presentation_config_v1 on a fresh account).
  const safeValue = value == null ? {} : value;
  if (snapshot.byKey && snapshot.userId === userId) {
    snapshot.byKey[key] = safeValue;
  }
  // Don't await — keep the persist effect non-blocking. Surface failures in
  // the console; in the next session the cold-load snapshot will reflect
  // whatever the last successful write was.
  supabase
    .from('user_state')
    .upsert({ user_id: userId, key, value: safeValue }, { onConflict: 'user_id,key' })
    .then(({ error }) => {
      if (error) console.warn(`[cloudBackup] write failed for ${key}:`, error.message);
    })
    .catch(err => {
      console.warn(`[cloudBackup] write threw for ${key}:`, err);
    });
}

// Drop the cached snapshot on sign-out so the next user doesn't read the
// previous user's keys before their own snapshot loads.
export function clearSnapshot() {
  snapshot = { userId: null, byKey: null };
}
